import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Box,
  Text,
  H2,
  Image,
  Anchor,
  Button,
  I18NLogOut,
  Grid,
  GridCell,
  Container,
  AnchorButton
} from '../../../components';
import { VerifyPhoneNumber } from '../../VerifyPhoneNumber';
import { useAuth } from '../../../lib/with-auth';
import messages from '../../VerifyPhoneNumber/messages';

import {
  productBySlugQuery_productBySlug,
  CurrentUser,
  VerificationMode
} from '../../../core-types';

type Props = {
  product: productBySlugQuery_productBySlug | null;
  privacyPageSlug: string | null;
  currentUser: CurrentUser | null;
  goNext: () => void;
};

const PhoneStep = ({
  product,
  privacyPageSlug,
  currentUser,
  goNext
}: Props) => {
  const { logOut } = useAuth();
  const intl = useIntl();

  if (!product) {
    return null;
  }

  let VerificationComponent: React.ReactElement | null = null;

  if (currentUser) {
    const { phoneNumber } = currentUser;
    VerificationComponent = (
      <>
        <Text as="p" data-testid="Checkout__CurrentUser__Alert">
          <FormattedMessage
            {...messages.alreadyLoggedIn}
            values={{
              phoneNumber: currentUser && currentUser.parsedPhoneNumber && (
                <Text as="span" bold>
                  {currentUser.parsedPhoneNumber.formattedNumber}
                </Text>
              )
            }}
          />
        </Text>
        {phoneNumber && (
          <Box mb="sm">
            <Button
              variant="primary"
              shape="cool"
              onClick={() => {
                goNext();
              }}
              data-testid="Checkout_Continue_With_Member"
            >
              <FormattedMessage {...messages.continueWithThisMember} />
            </Button>
          </Box>
        )}
        <Text as="p">
          <FormattedMessage
            {...messages.useOtherPhoneNumber}
            values={{
              logOutButton: (
                <AnchorButton
                  display="inline"
                  color="primary"
                  onClick={() => logOut()}
                >
                  <I18NLogOut />
                </AnchorButton>
              )
            }}
          />
        </Text>
      </>
    );
  } else {
    VerificationComponent = (
      <VerifyPhoneNumber
        mode={VerificationMode.signup}
        center={false}
        onLogIn={() => {
          goNext();
        }}
        label={
          <FormattedMessage
            {...messages.phoneNumberInfo}
            values={{
              privacyInformation: privacyPageSlug && (
                <Anchor href={`/${intl.locale}/${privacyPageSlug}`}>
                  <FormattedMessage {...messages.personalInformation} />
                </Anchor>
              )
            }}
          />
        }
      />
    );
  }

  return (
    <Container variant="wide" bleed>
      <Grid justifyContent="center">
        {product.image && product.image.url && (
          <GridCell width={[1, 1, 1, 2 / 5]} mb={6}>
            <Image
              boxShadow="heavy"
              borderRadius="lg"
              src={product.image.url}
            />
          </GridCell>
        )}
        <GridCell
          maxWidth="600px"
          width={[1, 1, 1, 3 / 5]}
          flexDirection="column"
          height="100%"
        >
          <Box flex={1} mb="md">
            <Text mt={[4, 4, 4, 0]}>
              <FormattedMessage
                id="modules.RedeemCoupon.PhoneStep.label"
                defaultMessage="The voucher code gives you:"
              />
            </Text>
            <H2 mb="xs" typoStyle="md">
              {product.name}
            </H2>
            {product.descriptionText && (
              <Text as="p" mb="sm">
                {product.descriptionText}
              </Text>
            )}
            {VerificationComponent}
          </Box>
        </GridCell>
      </Grid>
    </Container>
  );
};

export default PhoneStep;
